import React, { useRef, useEffect, useState } from 'react';
import { getPowerLinesHowerInfo } from './hoverPowerLines';
import { getSubstationLossInfo } from './hoverSubstationLoss';
import axios from 'axios';
import './HoverInfo.css';
import { BASE_URL } from '../../config';
import { getAccessToken } from '../LoginForm/Auth/auth';

const MAX_VISIBLE_ITEMS = 6;

const HoverInfo = ({ hoverInfo, substationOptions, lasLayers }) => {
  const hoverRef = useRef(null);
  const scrollRef = useRef(null);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [ohValue, setOhValue] = useState('N/A');
  const [powerLineInfo, setPowerLineInfo] = useState(null);
  const [substationLossInfo, setSubstationLossInfo] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const getLayerName = (featureId = '') => {
    if (!featureId || typeof featureId !== 'string') return '';
    return featureId.split('.')[0].split('-')[0];
  };

  useEffect(() => {
    const onDown = (e) => {
      if (e.key === 'Shift') setShowAll(true);
    };

    const onUp = (e) => {
      if (e.key === 'Shift') setShowAll(false);
    };

    const onBlur = () => setShowAll(false);

    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);

    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  useEffect(() => {
    if (showAll && scrollRef.current) {
      scrollRef.current.focus();
    }
  }, [showAll, hoverInfo]);

  useEffect(() => {
    if (!hoverInfo || !hoverInfo.object || !hoverRef.current) {
      return;
    }

    const buffer = 10;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const hoverWidth = hoverRef.current.offsetWidth;
    const hoverHeight = hoverRef.current.offsetHeight;

    let hoverX = hoverInfo.x + buffer;
    let hoverY = hoverInfo.y + buffer;

    if (hoverX + hoverWidth > windowWidth - buffer) {
      hoverX = windowWidth - hoverWidth - buffer;
    }

    if (hoverX < buffer) {
      hoverX = buffer;
    }

    // Prioritet: hover ispod kursora, a ako nema mjesta onda iznad
    if (hoverY + hoverHeight > windowHeight - buffer) {
      hoverY = hoverInfo.y - hoverHeight - buffer;
    }

    if (hoverY < buffer) {
      hoverY = buffer;
    }

    setPosition({ x: hoverX, y: hoverY });
  }, [hoverInfo, showAll, powerLineInfo, substationLossInfo, ohValue]);

  useEffect(() => {
    const fetchData = async () => {
      setPowerLineInfo(null);
      setSubstationLossInfo(null);
      setOhValue('N/A');

      if (!hoverInfo || !hoverInfo.object || !hoverInfo.object.id) {
        return;
      }

      const { id, properties } = hoverInfo.object;
      const layerName = getLayerName(id);

      if (layerName === 'mjerna_mjesta' && properties?.id_mm) {
        try {
          const token = getAccessToken();
          const response = await axios.get(`${BASE_URL}/geoserver/elektro/ows`, {
            params: {
              service: 'WFS',
              version: '1.0.0',
              request: 'GetFeature',
              typeName: 'elektro:mjerna_mjesta_mjerenja',
              outputFormat: 'application/json',
              cql_filter: `id_mm='${properties.id_mm}'`,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const features = response.data?.features || [];

          if (features.length > 0) {
            setOhValue(features[0]?.properties?.oh || 'N/A');
          } else {
            setOhValue('N/A');
          }
        } catch (error) {
          console.error(
            'Greška pri dohvaćanju podataka:',
            error.response ? error.response.data : error.message
          );
          setOhValue('Greška');
        }
      } else if (layerName.includes('dionica')) {
        const relevantInfo = await getPowerLinesHowerInfo(hoverInfo);
        setPowerLineInfo(relevantInfo);
      } else if (layerName.includes('trafo_stanice')) {
        const relevantInfo = await getSubstationLossInfo(hoverInfo);
        setSubstationLossInfo(relevantInfo);
      }
    };

    fetchData();
  }, [hoverInfo]);

  const handleWheel = (e) => {
    if (!showAll || !scrollRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    scrollRef.current.scrollTop += e.deltaY;
  };

  const handleKeyDown = (e) => {
    if (!showAll || !scrollRef.current) return;

    const el = scrollRef.current;
    const step = 24;
    const page = el.clientHeight * 0.8;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      el.scrollTop += step;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      el.scrollTop -= step;
    } else if (e.key === 'PageDown') {
      e.preventDefault();
      el.scrollTop += page;
    } else if (e.key === 'PageUp') {
      e.preventDefault();
      el.scrollTop -= page;
    }
  };

  if (!hoverInfo || !hoverInfo.object) {
    return null;
  }

  const { id, properties } = hoverInfo.object;
  const layerName = getLayerName(id);

  const renderInfoTable = (infoObject) => {
    if (!infoObject || typeof infoObject !== 'object') return null;

    const validEntries = Object.entries(infoObject).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== '' &&
        value !== 'N/A'
    );

    if (validEntries.length === 0) {
      return <div className="hover-empty-message">Nema dostupnih podataka</div>;
    }

    const entries = showAll
      ? validEntries
      : validEntries.slice(0, MAX_VISIBLE_ITEMS);

    const extraCount = validEntries.length - entries.length;

    return (
      <div
        ref={scrollRef}
        tabIndex={-1}
        onWheel={handleWheel}
        onKeyDown={handleKeyDown}
        className={`hover-scroll-container ${showAll ? 'expanded' : 'collapsed'}`}
      >
        <table className="hover-info-table">
          <tbody>
            {entries.map(([key, value]) => (
              <tr key={key}>
                <td>{key}:</td>
                <td>{String(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {!showAll && extraCount > 0 && (
          <div className="hover-expand-hint">
            Drži Shift za sve (+{extraCount})
          </div>
        )}

        {showAll && validEntries.length > MAX_VISIBLE_ITEMS && (
          <div className="hover-expand-hint">
            ↑/↓ ili PgUp/PgDn za skrol • pusti Shift za sažetak
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (layerName === 'mjerna_mjesta') {
      const relevantInfo = {
        'ID brojila': properties?.id_mm || 'N/A',
        'EIC broj': properties?.eic || 'N/A',
        'Čitački hod': ohValue,
        'Tarifna grupa': properties?.kategorija_potrosnje_grupa || 'N/A',
        'Poslovnica': properties?.terenska_jedinica || 'N/A',
        'Datum prijave MM': properties?.datum_upisa || 'N/A',
        'Trafo područje': properties?.pripadnost_ts_tp || 'N/A',
        'Adresa': properties?.adresa || 'N/A',
      };

      return renderInfoTable(relevantInfo);
    }

    if (layerName === 'stubovi') {
      const relevantInfo = {
        'ID stuba': properties?.id_stubnog_mjesta || 'N/A',
        'Tip stuba': properties?.tip_stuba || 'N/A',
        'Terenska jedinica': properties?.terenska_jedinica || 'N/A',
        'Koordinate':
          properties?.koordinata_x != null && properties?.koordinata_y != null
            ? `(${properties.koordinata_x}, ${properties.koordinata_y})`
            : 'N/A',
        'Pripadnost dionici': properties?.pripadnost_dionici || 'N/A',
        'Naziv topografski kljuc': properties?.naziv_topografski_kljuc || 'N/A',
        'Tip mreze': properties?.tip_mreze || 'N/A',
        'Poslednji zavrsen nalog za reviziju':
          properties?.poslednji_zavrsen_nalog_za_reviziju || 'N/A',
      };

      return renderInfoTable(relevantInfo);
    }

    if (layerName === 'dionica') {
      if (powerLineInfo) {
        return renderInfoTable(powerLineInfo);
      }
      return <div className="hover-loading">Učitavanje informacija...</div>;
    }

    if (layerName === 'trafo_stanice' && !substationOptions?.gubici_ts?.checked) {
      const relevantInfo = {
        'ID trafostanice': properties?.id_trafo_stanice || 'N/A',
        Naziv: properties?.naziv || 'N/A',
        Vlasnik: properties?.vlasnik || 'N/A',
        Adresa: properties?.adresa || 'N/A',
        'Godina izgradnje': properties?.godina_izgradnje || 'N/A',
        'Naziv topografski kljuc': properties?.naziv_topografski_kljuc || 'N/A',
        'Ukupna snaga kva': properties?.ukupna_snaga_kva || 'N/A',
        'Poslednji zavrsen nalog za reviziju':
          properties?.poslednji_zavrsen_nalog_za_reviziju || 'N/A',
      };

      return renderInfoTable(relevantInfo);
    }

    if (layerName === 'trafo_stanice' && substationOptions?.gubici_ts?.checked) {
      if (substationLossInfo) {
        return renderInfoTable(substationLossInfo);
      }
      return <div className="hover-loading">Učitavanje informacija...</div>;
    }

    if (layerName === 'vod') {
      const relevantInfo = {
        'ID voda': properties?.id_vod || 'N/A',
        'Naziv voda': properties?.naziv_voda || 'N/A',
        Dužina: properties?.duzina ?? 'N/A',
        'Broj stubnih mjesta u vodu': properties?.broj_stubnih_mjesta_u_vodu || 'N/A',
        'Šifra topografskog ključa': properties?.sifra_topografski_kljuc || 'N/A',
        'Datum upisa': properties?.datum_upisa || 'N/A',
        'Naponski nivo': properties?.naponski_nivo || 'N/A',
        'Poslednji zavrsen nalog za reviziju':
          properties?.poslednji_zavrsen_nalog_za_reviziju || 'N/A',
      };

      return renderInfoTable(relevantInfo);
    }

    const isLasLayer = lasLayers?.some((las) => hoverInfo.layer?.id === las.id);

    if (isLasLayer) {
      return (
        <div className="hover-las-content">
          <label>
            Koordinate: {`${hoverInfo.coordinate?.[0]}, ${hoverInfo.coordinate?.[1]}`}
          </label>
          <br />
          <label>Klasa: {hoverInfo.object?.classification}</label>
          <br />
          <label>Boja:</label>
          <br />
          <div
            style={{
              display: 'inline-block',
              width: '70px',
              height: '70px',
              backgroundColor: `rgb(${hoverInfo.object?.color?.[0] || 0}, ${hoverInfo.object?.color?.[1] || 0}, ${hoverInfo.object?.color?.[2] || 0})`,
              border: '1px solid black',
            }}
          />
        </div>
      );
    }

    const genericProperties = hoverInfo.object?.properties || {};
    return renderInfoTable(genericProperties);
  };

  return (
    <div
      ref={hoverRef}
      className="hover-info"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      {renderContent()}
    </div>
  );
};

export default HoverInfo;